import type { Unsubscribe } from "firebase/firestore";
import {
  markNotificationAsRead,
  watchNotifications,
} from "~/services/notifications";
import type { NotificationItem } from "~/types/notifications";

export const useNotificationStore = defineStore("notificationStore", () => {
  const notifications = ref<NotificationItem[]>([]);
  const isLoading = ref(true);
  const error = ref<string | null>(null);

  /** Number of unread notifications — drives the sidebar badge. */
  const unreadCount = computed(
    () => notifications.value.filter((n) => !n.read).length,
  );

  const hasUnread = computed(() => unreadCount.value > 0);

  // Active Firestore listener tear-down function.
  let _unsubscribe: Unsubscribe | null = null;

  /**
   * Starts the real-time Firestore listener for `userId`.
   * Safe to call multiple times — previous listener is torn down first.
   */
  function startWatching(userId: string) {
    stopWatching();

    isLoading.value = true;
    error.value = null;

    _unsubscribe = watchNotifications(
      userId,
      (items) => {
        notifications.value = items;
        isLoading.value = false;
      },
      (err) => {
        error.value = "Unable to load notifications.";
        isLoading.value = false;
        console.error("[notificationStore]", err);
      },
    );
  }

  /** Tears down the active Firestore listener and resets state. */
  function stopWatching() {
    _unsubscribe?.();
    _unsubscribe = null;
    notifications.value = [];
    isLoading.value = true;
    error.value = null;
  }

  /**
   * Marks a notification as read both locally (optimistic) and in Firestore.
   * No-op if already read.
   */
  async function markAsRead(userId: string, notificationId: string) {
    const item = notifications.value.find((n) => n.id === notificationId);
    if (!item || item.read) return;

    // Optimistic update so the UI responds immediately.
    item.read = true;

    try {
      await markNotificationAsRead(userId, notificationId);
    } catch (err) {
      // Roll back optimistic update on failure.
      item.read = false;
      console.error("[notificationStore] markAsRead failed", err);
    }
  }

  return {
    notifications,
    isLoading,
    error,
    unreadCount,
    hasUnread,
    startWatching,
    stopWatching,
    markAsRead,
  };
});
